import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import { useMatchDataProcessingStore } from "../../../store/MatchDataProcessingStore";
import { CompanyMatchUploadResponse } from "../../../types/companyMatch";
import { FileUploadFilter } from "../Whitespace/FileUploadFilter";
import { FileUploadFilterMode } from "../Whitespace/types";
import { useInsideFilterChip } from "../../../pages/Workspace/UserFilters/FiltersChips/hooks/useInsideFilterChip";
import { clearUploadedAccountsFilterState } from "../../../pages/Workspace/UserFilters/utils";
import { FilterLayout } from "./FilterLayout";

export const UploadedAccountsFilter = () => {
  const userFilters = useWorkspaceStore((state) => state.userFilters);
  const setUserFilters = useWorkspaceStore((state) => state.setUserFilters);
  const { draftFilters, setDraftFilters } = useFiltersContext();
  const { insideChip, elementRef } = useInsideFilterChip();

  // Get workspace ID and processing state
  const workspaceId = useWorkspaceStore((state) => state.draft?.id);
  const startProcessing = useMatchDataProcessingStore(
    (state) => state.startProcessing
  );
  const stopProcessing = useMatchDataProcessingStore(
    (state) => state.stopProcessing
  );
  const processingFile = useMatchDataProcessingStore((state) =>
    workspaceId
      ? state.getProcessingFile(workspaceId, "uploadedAccounts")
      : null
  );

  const selectedEntityDisplayOption = useMemo(() => {
    // If processing, use the value from processingFile
    if (processingFile) return processingFile?.filterMethod || "FILTER";

    return draftFilters?.companyMatch?.filterMethod || "FILTER";
  }, [draftFilters?.companyMatch?.filterMethod, processingFile]);

  const includeHierarchy = useMemo(() => {
    // If processing, use the value from processingFile
    if (processingFile) return processingFile?.includeHierarchy || false;

    return draftFilters?.companyMatch?.includeHierarchy || false;
  }, [draftFilters?.companyMatch?.includeHierarchy, processingFile]);

  const handleUpdateMatchData = (matchData: CompanyMatchUploadResponse) => {
    if (!workspaceId || !matchData.fileId) return;

    // Start processing for uploaded accounts - data will be stored only in localStorage
    // until processing is complete, then setUserFilters will be called by the polling hook
    // Store the current filter options with the match data
    const matchDataWithOptions = {
      ...matchData,
      fileName: matchData.fileName || "",
      filterMethod: selectedEntityDisplayOption,
      includeHierarchy,
    };
    startProcessing(workspaceId, matchDataWithOptions, "uploadedAccounts");
  };

  const handleClearFilter = () => {
    if (workspaceId) {
      stopProcessing(workspaceId, "uploadedAccounts");
    }
    setDraftFilters({ ...draftFilters, companyMatch: undefined });
    setUserFilters(clearUploadedAccountsFilterState(userFilters));
  };

  // Use processing data if available, otherwise use draftFilters.companyMatch
  const matchData = useMemo(() => {
    if (processingFile) {
      // While processing, show the file info from localStorage but keep the UI options
      return {
        ...processingFile,
        filterMethod: selectedEntityDisplayOption,
        includeHierarchy,
      };
    }

    return userFilters?.companyMatch || null;
  }, [
    processingFile,
    userFilters?.companyMatch,
    selectedEntityDisplayOption,
    includeHierarchy,
  ]);

  return (
    <FilterLayout
      title="Uploaded Accounts"
      onClear={handleClearFilter}
      showHeader={!insideChip}
      id="uploaded-accounts-filter"
      refProp={elementRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Stack>
        <Typography variant="caption" fontWeight="bold">
          Upload a List to View Specific Companies
        </Typography>
        <FileUploadFilter
          matchData={matchData}
          onUpdateMatchData={handleUpdateMatchData}
          mode={FileUploadFilterMode.UPLOAD_ACCOUNT_FILTER}
        />
      </Stack>
    </FilterLayout>
  );
};
